import { DataTable } from "primereact/datatable";
import { useTranslation } from "react-i18next";
import { Column } from "primereact/column";
import { InspectionsTableFields } from "./Fields";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getInspectionsThunk, inspectionsSelector } from "../../features/inspectionsSlice";
import {
    idwalGradeBodyTemplate, MultiSelectTemplate,
    RowWithSubtext,
    vesselBodyTemplate,
} from "@idwal/idwal-react-components";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useEffect, useRef, useState } from "react";
import { getUserInfoThunk, userSliceSelector } from "../../features/userSlice";
import { convertDateStringToUkLocale, getInspectionDate } from "../../utils/dates";
import { actionBodyTemplate } from "./actionsBodyTemplate";
import { useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import {
    INSPECTION_DATE_FILTER_FIELD,
    ISSUED_AT_FILTER_FIELD,
} from "./FilterFields";
import { InspectionDateFilterValue } from "../../types/inspections/InspectionDateFilterValue";
import TableHeader from "../table-header/TableHeader";
import { inspectionsTableGlobalFilterFields, idwalGradeFilterOptions, inspectionDateFilterOptions } from "../table-header/FilterSetup";
import { useDebounceCallback } from "usehooks-ts";
import {
    inspectionDateColumn,
    inspectionTypeColumn,
    vesselColumn,
    vesselTypeColumn
} from "../shared-columns/shared-columns";
import { InProgressTableRow } from "../inprogress-table/InProgressTable";
import { DEFAULT_ROWS_PER_PAGE, DEFAULT_ROWS_PER_PAGE_OPTIONS } from "../../constants/TableConstants";
import { Inspection } from "../../types/inspections/Inspection";
import { useFlags } from "launchdarkly-react-client-sdk";

export const FULL_REPORT_STATUS = "FULL_REPORT";
export const CANCELLED_STATUS = "CANCELLED";
export const PURCHASED_REPORT_SHORT_NAME = "PSR";
const INSPECTIONS_TABLE_KEY = "inspectionsTable";

interface InspectionsTableRow {
    vesselName: string;
    imo: string;
    vesselNameFilterField: string;
    inspectionDateFilterField: InspectionDateFilterValue;
    issuedDateFilterField: InspectionDateFilterValue;
    inspectionType: string;
    inspectionNumber: string;
    port: string;
    inspectionDate: string;
    idwalGrade: string;
    inspectionStatusFriendlyName: string;
    inspectionStatus: string;
    inspectionGuid: string;
    issuedAt: string;
    vesselType: string;
    issuedAtSortField: string;
    inspectionDateSortField: string;
}

const defaultFilters = () => ({
    global: { value: "", matchMode: FilterMatchMode.CONTAINS },
    vesselNameFilterField: { value: null, matchMode: FilterMatchMode.CONTAINS },
    inspectionType: { value: null, matchMode: FilterMatchMode.IN },
    inspectionDateFilterField: { value: null, matchMode: FilterMatchMode.CUSTOM },
    issuedDateFilterField: { value: null, matchMode: FilterMatchMode.CUSTOM },
    vesselType: { value: null, matchMode: FilterMatchMode.IN },
    idwalGrade: { value: null, matchMode: FilterMatchMode.CUSTOM },
});

function InspectionsTable() {
    const { t } = useTranslation("locale");
    const {Inspections, InspectionsStatus} = useAppSelector(inspectionsSelector);
    const {UserInfoStatus, SelectedGroupName, OrganisationsInfo, OrganisationsInfoStatus} = useAppSelector(userSliceSelector);
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
    const [_globalFilterValue, setGlobalFilterValue] = useState("");
    const ref = useRef(null)
    const [debouncedSearchValue, setDebouncedSearchValue] = useState("");
    const debounced = useDebounceCallback((value: string) => {
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
    }, 300);

    const {
        releaseResales
    } = useFlags();

    const onPage = (event: any) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const [filters, setFilters] = useState(defaultFilters());

    const onGlobalFilterChange = (e: any) => {
        const value = e.target.value;

        setDebouncedSearchValue(value);
        debounced.cancel();
        debounced(value);
    };

    useEffect(() => {
        if (UserInfoStatus === "succeeded" && OrganisationsInfoStatus === "succeeded") {
            dispatch(getInspectionsThunk({orgIds: [SelectedGroupName !== "" ? SelectedGroupName : OrganisationsInfo[0].id.toString()], user}));
        }

    }, [dispatch, UserInfoStatus, SelectedGroupName, OrganisationsInfo]);

    useEffect(() => {
        if (user && UserInfoStatus === "idle") {
            dispatch(getUserInfoThunk({userId: user.username!}));
        }
    }, [user]);

    const inspectionFilter = ((inspection: Inspection) => inspection.status === FULL_REPORT_STATUS && (releaseResales || inspection.inspectionTypeShortName !== PURCHASED_REPORT_SHORT_NAME));

    const values: InspectionsTableRow[] = Inspections.filter(inspectionFilter).map(inspection => ({
            vesselName: inspection.vesselName,
            vesselNameFilterField: `${inspection.vesselName} ${inspection.vesselImo}`,
            inspectionDateFilterField: {dateFrom: inspection.inspectionDateFrom, dateTo: inspection.inspectionDateTo},
            issuedDateFilterField: {dateFrom: inspection.issuedAt, dateTo: undefined},
            vesselType: inspection.vesselType,
            imo: inspection.vesselImo,
            inspectionType: inspection.inspectionType,
            inspectionNumber: inspection.inspectionNumber,
            port: inspection.port,
            inspectionDate: getInspectionDate(inspection.inspectionDateFrom, inspection.inspectionDateTo),
            idwalGrade: inspection.overallScore,
            inspectionStatus: inspection.status,
            inspectionStatusFriendlyName: t(inspection.status),
            inspectionGuid: inspection.id,
            issuedAt: convertDateStringToUkLocale(inspection.issuedAt),
            issuedAtSortField: inspection.issuedAt,
            inspectionDateSortField: inspection.inspectionDateFrom,
            location: inspection.port + ", " + inspection.country
        }
    ));
    const getValues = (): InspectionsTableRow[] => values;

    const distinctTypes = Array.from(new Set([...values.flatMap((e: InspectionsTableRow) => e.inspectionType)]));
    const distinctVesselTypes = Array.from(new Set([...values.flatMap((e: InspectionsTableRow) => e.vesselType)]));

    const resetTable = () => {
        sessionStorage.removeItem(INSPECTIONS_TABLE_KEY);
        // @ts-ignore due to type difficulties with ref.current.reset - I don't think it's an important enough type to spend too long researching
        ref.current?.reset();
        onFilterFunction({filters: defaultFilters()});
    };

    const onFilterFunction = (e: any) => {
        setFilters(e.filters);
        setDebouncedSearchValue(e.filters.global.value)
    }

    return <div className={"table-container home-table"}  data-cy="inspections-table-container">
        <DataTable
            emptyMessage={t("inspections.table.emptyTable")}
            removableSort
            stripedRows
            globalFilterFields={inspectionsTableGlobalFilterFields}
            header={TableHeader({setGlobalFilterValue, onGlobalFilterChange, globalFilterValue: debouncedSearchValue, resetTable})}
            paginator
            scrollable
            first={first}
            onPage={onPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            scrollHeight="55vh"
            rows={rows}
            stateStorage="session"
            stateKey={INSPECTIONS_TABLE_KEY}
            filters={filters}
            filterDisplay="row"
            // @ts-ignore
            onFilter={onFilterFunction}
            value={getValues()}
            sortField={InspectionsTableFields.ISSUED_AT_SORT_FIELD}
            sortOrder={-1}
            loading={InspectionsStatus === "pending"}
            ref={ref}
        >
            {vesselColumn(
                InspectionsTableFields.VESSEL,
                (data: InProgressTableRow) => vesselBodyTemplate(data, () => {}, false))}
            <Column
                field={InspectionsTableFields.ACTIONS}
                header={t("inspections.table.actions")}
                body={(data: InspectionsTableRow) =>  
                    actionBodyTemplate(
                        data,
                        (data: InspectionsTableRow) => navigate(`/inspection/${data.inspectionGuid}`),
                        t
                    )
                }
            />
            <Column
                field={InspectionsTableFields.GRADE}
                header={t("inspections.table.gradeHeader")}
                body={(data: InspectionsTableRow) =>  idwalGradeBodyTemplate({idwalGrade: data.idwalGrade, imo: data.imo, status: data.inspectionStatus, guid: data.inspectionGuid}, false,`/inspection/${data.inspectionGuid}`,true)}
                filter
                filterField={InspectionsTableFields.GRADE}
                showFilterMenu={false}
                filterElement={(options) => MultiSelectTemplate(options, idwalGradeFilterOptions, t("inspections.table.showAll"), "grade-filter")}
                sortable
            />
            {inspectionTypeColumn(distinctTypes)}
            <Column
                field={InspectionsTableFields.ISSUED_AT_SORT_FIELD}
                header={t("inspections.table.statusHeader")}
                body={(data: InspectionsTableRow) =>  RowWithSubtext(data, InspectionsTableFields.STATUS_FRIENDLY_NAME, InspectionsTableFields.ISSUED_AT)}
                filter
                filterField={ISSUED_AT_FILTER_FIELD}
                showFilterMenu={false}
                filterElement={(options) => MultiSelectTemplate(options, inspectionDateFilterOptions, t("inspections.table.showAll"), "inspection-status-filter")}
                sortable
            />
            {vesselTypeColumn(
                InspectionsTableFields.VESSEL_TYPE,
                distinctVesselTypes)
            }
            {inspectionDateColumn(
                InspectionsTableFields.INSPECTION_DATE_SORT_FIELD,
                INSPECTION_DATE_FILTER_FIELD,
                (data: InProgressTableRow) => 
                    RowWithSubtext(
                        data,
                        InspectionsTableFields.INSPECTION_DATE,
                        InspectionsTableFields.LOCATION
                    ),
                inspectionDateFilterOptions
            )}
        </DataTable>
    </div>

}

export default InspectionsTable;
